import type { CalcStep } from "@/lib/calc/steps";

export interface FeeInputs {
  price: number; cost: number;
  shippingCharged: number;   // paid by the buyer, on top of price
  shippingCost: number;      // what you pay the carrier to send one order
  feePct: number;            // marketplace % fee (referral / final value / transaction)
  feeOnShipping: boolean;    // most marketplaces charge their % on shipping too
  fixedFee: number;          // per-order fixed fees
  paymentPct: number; paymentFixed: number; // separate payment processor, 0 where the marketplace fee already includes it
  fulfilment: number;        // e.g. Amazon FBA fee per unit
  cac: number;
  returnPct: number;         // share of orders returned
  returnShipping: number;    // what a return costs you in postage
  unsellablePct: number;     // share of returned items you can't resell
  /** Sales tax collected on the order. Added to the fee base only where the marketplace charges its fee on it (eBay does). Default 0. */
  salesTaxInFeeBase?: number;
  /** A lower rate on the part of the fee base above `above` (eBay: 2.35% above $7,500 per item). Default none. */
  feeTier?: { above: number; pct: number };
  /** Etsy Offsite Ads (CAP-384-1): % of price + shipping charged on orders that came from an ad click, capped per order. Default off. */
  offsiteAdsPct?: number;
  /** Per-order cap on the Offsite Ads fee (Etsy: $100). Default no cap. */
  offsiteAdsCap?: number;
}

export interface FeeResult {
  marketplaceFee: number; paymentFee: number; fixed: number;
  /** Offsite Ads fee on this order (0 unless offsiteAdsPct is set). Already counted in profitIfKept and lossIfReturned. */
  offsiteAds: number;
  profitIfKept: number; lossIfReturned: number;
  expectedProfit: number; margin: number | null; breakEvenPrice: number | null;
}

/**
 * Per-order expected profit.
 *  Kept order:     price + shipping charged − cost − all fees − shipping − fulfilment − CAC
 *  Returned order: the buyer is refunded in full and you keep the item (if resellable), but you still lose
 *                  outbound + return postage, the fixed and payment-processor fees, fulfilment and CAC.
 *                  The marketplace % fee is assumed refunded. Unsellable returns also lose the item's cost.
 * More returns therefore always lowers profit (or deepens a loss).
 */
export function feeModel(i: FeeInputs): FeeResult {
  const base = i.price + (i.feeOnShipping ? i.shippingCharged : 0) + Math.max(0, i.salesTaxInFeeBase ?? 0);
  const marketplaceFee = i.feeTier && base > i.feeTier.above
    ? (i.feeTier.above * i.feePct + (base - i.feeTier.above) * i.feeTier.pct) / 100
    : base * i.feePct / 100;
  const gross = i.price + i.shippingCharged;
  const paymentFee = gross * i.paymentPct / 100 + i.paymentFixed;
  const fixed = i.fixedFee;
  // Offsite Ads: assumed NOT refunded when the order is returned (the higher-cost reading; Etsy's page does not say).
  const offsiteRaw = (i.offsiteAdsPct ?? 0) > 0 ? gross * (i.offsiteAdsPct ?? 0) / 100 : 0;
  const offsiteAds = i.offsiteAdsCap !== undefined && i.offsiteAdsCap >= 0 ? Math.min(offsiteRaw, i.offsiteAdsCap) : offsiteRaw;
  const profitIfKept = gross - i.cost - marketplaceFee - paymentFee - fixed - offsiteAds - i.shippingCost - i.fulfilment - i.cac;
  const lossIfReturned = -(i.shippingCost + i.returnShipping + paymentFee + fixed + offsiteAds + i.fulfilment + i.cac + i.cost * i.unsellablePct / 100);
  const r = Math.min(Math.max(i.returnPct, 0), 100) / 100;
  const expectedProfit = profitIfKept * (1 - r) + lossIfReturned * r;
  const revenue = gross * (1 - r);
  return { marketplaceFee, paymentFee, fixed, offsiteAds, profitIfKept, lossIfReturned, expectedProfit, margin: revenue > 0 ? (expectedProfit / revenue) * 100 : null, breakEvenPrice: null };
}

/**
 * Reverse solve: the selling price that gives `targetMarginPct` on the same "margin on revenue kept" figure the
 * calculator shows (expected profit / expected revenue after returns). Expected profit is linear in the price, so two
 * evaluations are enough. Null when the margin can't be reached (fees and margin together swallow the whole price),
 * or when every order is returned.
 */
export function suggestedPrice(i: FeeInputs, targetMarginPct: number): number | null {
  const r = Math.min(Math.max(i.returnPct, 0), 100) / 100;
  if (!Number.isFinite(targetMarginPct) || targetMarginPct >= 100 || r >= 1) return null;
  const m = targetMarginPct / 100;
  const f = (price: number) => {
    const res = feeModel({ ...i, price });
    return res.expectedProfit - m * (price + i.shippingCharged) * (1 - r);
  };
  const f0 = f(0), f1 = f(1);
  const slope = f1 - f0;
  if (!(slope > 1e-9)) return null;
  const p = -f0 / slope;
  return Number.isFinite(p) && p > 0 ? Math.round(p * 100) / 100 : null;
}

export type FeeFieldKey = "price" | "cost" | "feePct" | "fixedFee" | "payPct" | "payFixed" | "shipCharged" | "shipCost" | "fulfil" | "cac" | "returnPct" | "returnShip" | "unsellable";

const PERCENT_FIELDS: FeeFieldKey[] = ["feePct", "payPct", "returnPct", "unsellable"];

/** Range problems for already-parsed values (NaN is handled separately as "not a number"). Negative amounts and percentages outside 0-100 are rejected; price must be above 0. */
export function feeRangeErrors(vals: Record<FeeFieldKey, number>): Partial<Record<FeeFieldKey, string>> {
  const out: Partial<Record<FeeFieldKey, string>> = {};
  for (const k of Object.keys(vals) as FeeFieldKey[]) {
    const v = vals[k];
    if (!Number.isFinite(v)) continue;
    if (PERCENT_FIELDS.includes(k)) { if (v < 0 || v > 100) out[k] = "Enter a percentage between 0 and 100."; }
    else if (v < 0) out[k] = "This can't be negative.";
  }
  if (Number.isFinite(vals.price) && vals.price === 0) out.price = "Enter a selling price above 0.";
  return out;
}

// ---------------------------------------------------------------------------------------------------------------------
// B-0125: every fee on its own line, so the cards on screen add up to the profit shown.
// ---------------------------------------------------------------------------------------------------------------------
export interface FeeLine { label: string; amount: number; kind: "plus" | "minus" | "total" }
const c2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

/** The kept order, line by line. Amounts are rounded to the cent; a "Rounding" line carries any 1-cent difference so the lines add up to the profit shown. */
export function keptOrderLines(i: FeeInputs, r: FeeResult): FeeLine[] {
  const plus = (label: string, n: number): FeeLine[] => (n !== 0 ? [{ label, amount: c2(n), kind: "plus" as const }] : []);
  const minus = (label: string, n: number): FeeLine[] => (n !== 0 ? [{ label, amount: -c2(n), kind: "minus" as const }] : []);
  const lines: FeeLine[] = [
    { label: "Selling price", amount: c2(i.price), kind: "plus" },
    ...plus("Shipping charged to the buyer", i.shippingCharged),
    ...minus("Your cost", i.cost),
    ...minus("Your shipping cost", i.shippingCost),
    ...minus("Marketplace fee", r.marketplaceFee),
    ...minus("Per-order fixed fees", r.fixed),
    ...minus(i.paymentFixed > 0 && i.paymentPct > 0 ? `Payment processing (${i.paymentPct}% + fixed ${c2(i.paymentFixed)})` : "Payment processing", r.paymentFee),
    ...minus("Etsy Offsite Ads fee", r.offsiteAds),
    ...minus("Fulfilment fee", i.fulfilment),
    ...minus("Ad / acquisition cost", i.cac),
  ];
  const sum = c2(lines.reduce((a, l) => a + l.amount, 0));
  const profit = c2(r.profitIfKept);
  const diff = c2(profit - sum);
  if (diff !== 0) lines.push({ label: "Rounding (each line is rounded to the cent)", amount: diff, kind: diff > 0 ? "plus" : "minus" });
  lines.push({ label: "Profit if the order is kept", amount: profit, kind: "total" });
  return lines;
}

/** The returned order, line by line, ending at the result shown for a return. */
export function returnedOrderLines(i: FeeInputs, r: FeeResult): FeeLine[] {
  const minus = (label: string, n: number): FeeLine[] => (n !== 0 ? [{ label, amount: -c2(n), kind: "minus" as const }] : []);
  const lines: FeeLine[] = [
    ...minus("Your outbound shipping", i.shippingCost), ...minus("Return postage", i.returnShipping), ...minus("Payment processing (not refunded)", r.paymentFee),
    ...minus("Per-order fixed fees", r.fixed), ...minus("Etsy Offsite Ads fee", r.offsiteAds), ...minus("Fulfilment fee", i.fulfilment), ...minus("Ad / acquisition cost", i.cac), ...minus("Unsellable items (their cost)", i.cost * i.unsellablePct / 100),
  ];
  const sum = c2(lines.reduce((a, l) => a + l.amount, 0));
  const total = c2(r.lossIfReturned);
  const diff = c2(total - sum);
  if (diff !== 0) lines.push({ label: "Rounding (each line is rounded to the cent)", amount: diff, kind: diff > 0 ? "plus" : "minus" });
  lines.push({ label: "Result if the order is returned", amount: total, kind: "total" });
  return lines;
}

type Fmt = (n: number) => string | number;
export function feeSteps(i: FeeInputs, r: FeeResult, fmt: Fmt): CalcStep[] {
  const base = i.price + (i.feeOnShipping ? i.shippingCharged : 0) + Math.max(0, i.salesTaxInFeeBase ?? 0);
  const rr = Math.min(Math.max(i.returnPct, 0), 100) / 100;
  const steps: CalcStep[] = [
    { label: "Marketplace fee", formula: i.feeTier && base > i.feeTier.above ? "(fee base up to the threshold x fee %) + (fee base above it x tier %)" : "Fee base x fee %", inputs: { "Fee base": fmt(base), "Fee %": `${i.feePct}%`, ...(i.feeTier ? { Threshold: fmt(i.feeTier.above), "Tier %": `${i.feeTier.pct}%` } : {}) }, result: fmt(r.marketplaceFee) },
    { label: "Payment processing", formula: "(Price + shipping charged) x processing % + processing fixed", inputs: { "Price + shipping": fmt(i.price + i.shippingCharged), "Processing %": `${i.paymentPct}%`, "Processing fixed": fmt(i.paymentFixed) }, result: fmt(r.paymentFee) },
    { label: "Profit if the order is kept", formula: "Price + shipping charged - cost - marketplace fee - processing - per-order fixed fees - Offsite Ads fee - your shipping - fulfilment - ad cost", inputs: { Price: fmt(i.price), "Marketplace fee": fmt(r.marketplaceFee), Processing: fmt(r.paymentFee), "Per-order fixed fees": fmt(r.fixed) }, result: fmt(r.profitIfKept) },
    { label: "Result if the order is returned", formula: "-(outbound shipping + return postage + processing + fixed fees + fulfilment + ad cost + cost of unsellable items)", inputs: { "Return postage": fmt(i.returnShipping), Processing: fmt(r.paymentFee) }, result: fmt(r.lossIfReturned) },
    { label: "Expected profit per order", formula: "Profit if kept x (1 - return rate) + result if returned x return rate", inputs: { "Profit if kept": fmt(r.profitIfKept), "Result if returned": fmt(r.lossIfReturned), "Return rate": `${i.returnPct}%` }, result: fmt(r.expectedProfit) },
  ];
  if (r.margin !== null) steps.push({ label: "Margin on revenue kept", formula: "Expected profit / ((price + shipping charged) x (1 - return rate))", inputs: { "Expected profit": fmt(r.expectedProfit), "Revenue kept": fmt((i.price + i.shippingCharged) * (1 - rr)) }, result: `${r.margin.toFixed(1)}%` });
  return steps;
}

/** The message under the results when a value is out of range: names the real problem instead of one catch-all sentence. */
export function feeErrorMessage(errors: Partial<Record<FeeFieldKey, string>>): string {
  const keys = Object.keys(errors) as FeeFieldKey[];
  if (keys.length === 1 && keys[0] === "price") return "Enter a selling price above 0.";
  const names: Record<FeeFieldKey, string> = { price: "selling price", cost: "your cost", feePct: "marketplace fee", fixedFee: "per-order fixed fees", payPct: "payment processing %", payFixed: "processing fixed", shipCharged: "shipping charged", shipCost: "your shipping cost", fulfil: "fulfilment fee", cac: "ad cost", returnPct: "return rate", returnShip: "return postage", unsellable: "unsellable returns" };
  const parts = keys.map((k) => (k === "price" ? "the selling price must be above 0" : `${names[k]}: ${errors[k]!.replace(/\.$/, "").toLowerCase()}`));
  return `Check ${parts.join("; ")}.`;
}

/** When each preset's rates were last compared with the marketplace's own page, and whether that happened in the latest review. */
export interface RatesCheck { month: string; official: boolean; source?: string; /** The day the figures were read from the marketplace's own page (ISO), when known. */ on?: string }
export function ratesCheckedLine(c: RatesCheck): string {
  return c.official
    ? `Rates checked ${c.month}${c.source ? ` (${c.source})` : ""}.`
    : `Rates entered ${c.month}; not re-checked against the marketplace's own page since, so confirm them before you rely on them.`;
}

// ---------------------------------------------------------------------------------------------------------------------
// CAP-384-1: ROI, break-even price and monthly profit next to the margin. Additive: nothing above changes.
// ---------------------------------------------------------------------------------------------------------------------
/** What you put in per order: cost + your shipping + ad cost (ad / acquisition cost plus any Offsite Ads fee). */
export function feeOutlay(i: FeeInputs, r: FeeResult): number { return i.cost + i.shippingCost + i.cac + r.offsiteAds; }

/** ROI in percent = profit if the order is kept / (cost + own shipping + ad cost). Null when the outlay is 0. */
export function feeRoi(i: FeeInputs, r: FeeResult): number | null {
  const out = feeOutlay(i, r);
  return out > 0 ? (r.profitIfKept / out) * 100 : null;
}

/**
 * The lowest selling price (rounded up to the cent) at which the expected profit per order is not negative, with the same
 * fees, shipping and return rate. Solved by bisection because the eBay tier and the Offsite Ads cap bend the line.
 * Null when no price can cover the fees (percentage fees at or above 100%, or every order returned).
 */
export function breakEvenPrice(i: FeeInputs): number | null {
  const profit = (price: number) => feeModel({ ...i, price }).expectedProfit;
  if (Math.min(Math.max(i.returnPct, 0), 100) >= 100) return null;
  let hi = Math.max(1, i.cost + i.shippingCost + i.fixedFee + i.fulfilment + i.cac + i.paymentFixed + i.returnShipping);
  let n = 0;
  while (profit(hi) < 0 && n++ < 60) hi *= 2;
  if (profit(hi) < 0) return null;
  let lo = 0;
  for (let k = 0; k < 80; k++) { const mid = (lo + hi) / 2; if (profit(mid) < 0) lo = mid; else hi = mid; }
  return Math.ceil(hi * 100 - 1e-6) / 100;
}

/** Expected profit for a month of orders. Null when the volume is not a positive whole number. */
export function monthlyProfit(r: FeeResult, orders: number): number | null {
  return Number.isFinite(orders) && orders > 0 ? c2(r.expectedProfit * Math.floor(orders)) : null;
}
